import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { RemoveDuplicateRows } from './removeDuplicateRows';
import { Paper, Typography, Box, Button } from '@mui/material';
import { useIntl } from 'react-intl';
import * as TFF from '@tff/types/tff';
import React, { useEffect, useState } from 'react';
import useStyles from './AccordionStyles';
import { groupNewSsrsByBound } from './Utils/AncillaryCostTable/groupNewSsrsByBound';
import { insertSeatsToRows } from './Utils/AncillaryCostTable/insertSeatsToRows';
import { populateRowsWithData } from './Utils/AncillaryCostTable/populateRowsWithData';
import AncillaryCostTableRows from './AncillaryCostTableRows';
import { GenericRowType, RowsType } from '../../types/ancillaryCostTableRowTypes';
import { SsrsByPax, SurchargeType } from '../../types/ancillaryTypes';
import { MetaValue } from '@tff/types/tff/tfa-amend-ancillary-service';
import { useBookingContext } from '../../hooks/use-booking-context';

const commonStyles = {
  cellHeader: {
    color: '#4D4D4D',
    fontSize: '18px',
    lineHeight: '20.59px',
    fontWeight: 700,
    width: '364px',
  },
  cellBody: {
    color: '#4D4D4D',
    fontSize: '18px',
    lineHeight: '20.59px',
    fontWeight: 700,
  },
  typography: {
    textAlign: 'left',
  },
  errorTypography: {
    textAlign: 'left',
    fontWeight: 'bold',
    color: '#D30D14',
  },
};

interface props {
  flightDetails: TFF.FlightDetails;
  newAddedSsrs: SsrsByPax;
  includedSsrs: SsrsByPax;
  outboundMetaValues: MetaValue[];
  inboundMetaValues: MetaValue[];
  rebooking?: boolean;
  rebookOffers?: any;
  setDisplayTable: (value: boolean) => void;
  submitChanges: () => void;
  setAdjustableSurcharge: React.Dispatch<React.SetStateAction<SurchargeType>>;
  amendBalanceDue?: number;
  outboundFlightOnd: string;
  returnFlightOnd: string;
  isNskBooking: boolean | undefined;
}

const AncillaryCostTable: React.FC<props> = ({
  flightDetails,
  newAddedSsrs,
  includedSsrs,
  outboundMetaValues,
  inboundMetaValues,
  rebooking,
  rebookOffers,
  setDisplayTable,
  submitChanges,
  setAdjustableSurcharge,
  amendBalanceDue,
  outboundFlightOnd,
  returnFlightOnd,
  isNskBooking,
}) => {
  const intl = useIntl();
  const { currency, numberFormatter } = useBookingContext();
  const originalAncillaries: TFF.Ancillaries = flightDetails.OrderDetails.Ancillaries!;
  const [totalCost, setTotalCost] = useState<number>(0);

  const classes = useStyles();

  let rows: RowsType = [
    { label: `${intl.formatMessage({ id: 'rebook.directionOutbound' })}`, isSection: true },
    { label: `${intl.formatMessage({ id: 'rebook.directionInbound' })}`, isSection: true },
    {
      original: `${intl.formatMessage({ id: 'ndc.rebookTax' })}`,
      new: `${intl.formatMessage({ id: 'ndc.rebookTax' })}`,
      surcharge: 0.0,
    },
    {
      original: `${intl.formatMessage({ id: 'ndc.rebookRefundable' })}`,
      new: `${intl.formatMessage({ id: 'ndc.rebookAirline' })}`,
      surcharge: 0.0,
    },
    {
      original: `${intl.formatMessage({ id: 'ndc.rebookRefundableExtras' })}`,
      new: `${intl.formatMessage({ id: 'ndc.rebookServiceFee' })}`,
      surcharge: 0.0,
    },
    {
      sumCancelled: `${intl.formatMessage({ id: 'ndc.rebookSumCancelled' })}`,
      sumNew: `${intl.formatMessage({ id: 'ndc.rebookSumNew' })}`,
      total: `${intl.formatMessage({ id: 'ndc.rebookTotalToPay' })}`,
      surcharge: 0.0,
      isSummary: true,
    },
  ];

  const [surchargeValues, setSurchargeValues] = useState({});

  // Can be for two cases:
  // User changes Service Fee
  // In rebook or NSK edit ssrs, user changes adjusted surcharge
  const handleInputChange = (id, value) => {
    setSurchargeValues(prevValues => ({
      ...prevValues,
      [id]: value,
    }));
  };

  useEffect(() => {
    setAdjustableSurcharge(surchargeValues);
  }, [surchargeValues]);

  useEffect(() => {
    setAdjustableSurcharge(surchargeValues);
  }, [amendBalanceDue, totalCost]);

  const sumSurchargeValues = (surchargeValues: { [key: string]: string | number }) => {
    return Object.values(surchargeValues).reduce(
      (sum: number, value: string | number) => sum + parseFloat(value.toString().replace(',', '.')),
      0,
    );
  };

  populateRowsWithData(
    groupNewSsrsByBound(newAddedSsrs, outboundFlightOnd, returnFlightOnd),
    rows,
    intl.formatMessage({ id: 'rebook.directionOutbound' }),
    intl.formatMessage({ id: 'rebook.directionInbound' }),
    outboundMetaValues,
    inboundMetaValues,
    includedSsrs,
    outboundFlightOnd,
    intl,
    originalAncillaries,
    rebooking,
  );

  /** Insert retrieved flight offers into the table below */
  insertSeatsToRows(rebooking, rebookOffers, rows, intl.formatMessage({ id: 'rebook.directionInbound' }));

  //this is a temporary solution --- find out why duplication happens
  const uniqueRows = RemoveDuplicateRows(rows);

  useEffect(() => {
    uniqueRows.forEach(row => {
      if (row.original && row.new && row.new.length > 0) {
        setSurchargeValues(prevValues => ({
          ...prevValues,
          [`${row.code}-${row.new[0]}-${row.original[0]}`]: numberFormatter.format(row.surcharge),
        }));
      }
    });
  }, []);

  useEffect(() => {
    if (!rebookOffers && showAdjustedSurcharge()) {
      setTotalCost(Number(sumSurchargeValues(surchargeValues)));
      return;
    }

    const outboundTotal =
      rebookOffers?.seatRows?.outboundRows?.length > 0
        ? rebookOffers.seatRows.outboundRows.reduce((sum, i) => sum + (i.surcharge || 0), 0)
        : 0;

    const inboundTotal =
      rebookOffers?.seatRows?.inboundRows?.length > 0
        ? rebookOffers.seatRows.inboundRows.reduce((sum, i) => sum + (i.surcharge || 0), 0)
        : 0;

    const seatPrices = outboundTotal + inboundTotal;

    setTotalCost(sumSurchargeValues(surchargeValues) + seatPrices);
  }, [surchargeValues]);

  const handleConfirmChanges = () => {
    submitChanges();
  };

  const showAdjustedSurcharge = () => {
    return rebooking || isNskBooking;
  };

  return (
    <>
      <Paper sx={{ marginTop: 2, padding: 3, minWidth: 650, width: '100%', overflow: 'hidden' }}>
        <Typography
          sx={{
            fontSize: '27px',
            color: '#09295D',
            fontWeight: 700,
            lineHeight: '30.89px',
            paddingTop: '16px',
            paddingBottom: 3,
          }}
        >
          {intl.formatMessage({ id: 'ndc.rebookCostOverview' })}
        </Typography>
        <TableContainer>
          <Table aria-label="caption table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: '#E7E2DA',
                    ...commonStyles.cellHeader,
                  }}
                >
                  {intl.formatMessage({ id: 'rebook.Original' })}
                </TableCell>
                <TableCell align="center" sx={{ ...commonStyles.cellHeader, backgroundColor: '#E2F5FD' }}>
                  {intl.formatMessage({ id: 'ndc.rebookNewBooking' })}
                </TableCell>
                <TableCell align="center" sx={{ ...commonStyles.cellBody }}>
                  {intl.formatMessage({ id: 'ndc.rebookSurcharge' })}
                </TableCell>
                {showAdjustedSurcharge() && (
                  <TableCell align="center" sx={{ ...commonStyles.cellBody }}>
                    {intl.formatMessage({ id: 'ndc.rebookAdjustedSurcharge' })}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {uniqueRows.map((row: GenericRowType, index: string) => (
                <AncillaryCostTableRows
                  key={index}
                  row={row}
                  index={index}
                  surchargeValues={surchargeValues}
                  rebooking={rebooking}
                  totalCost={totalCost}
                  handleInputChange={handleInputChange}
                  commonStyles={commonStyles}
                  uniqueRows={uniqueRows}
                  showAdjustedSurcharge={showAdjustedSurcharge()}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <caption style={{ marginTop: '16px', width: '137px', fontSize: '12px' }}>
          {`All Prices are in ${currency}`}
        </caption>
      </Paper>
      {!rebooking && (
        <>
          <Box className={classes.ancillaryFooter}>
            <Button color="info" className={classes.ancillaryCancel} onClick={() => setDisplayTable(false)}>
              {intl.formatMessage({ id: 'ancillaries.action.cancel' })}
            </Button>
            <Button
              className={classes.ancillaryAccept}
              onClick={() => {
                handleConfirmChanges();
              }}
            >
              {intl.formatMessage({ id: 'ancillaries.action.confirmChanges' })}
            </Button>
          </Box>
        </>
      )}
    </>
  );
};
export default AncillaryCostTable;
